import { type LandingPageData } from '#app/config/schemas/landingPageSchema'

const DEFAULT_FREE_TRIAL_CTA_TARGETS = new Set([
	'/signup',
	'/programs',
	'/roadmaps',
	'/collections',
])

type LandingBlock = {
	type?: string
	props?: Record<string, unknown>
	[key: string]: unknown
}

type LandingPageBlock = LandingPageData['content'][number]

function resolveCtaHref(
	href: unknown,
	ctaTargetPath: string,
	ctaTargets: Set<string>,
) {
	if (typeof href !== 'string') return href
	if (!ctaTargets.has(href)) return href
	return ctaTargetPath
}

export function enhanceLandingFreeTrialCtas(params: {
	landingPageData: LandingPageData
	ctaTargetPath: string | null
	ctaTargets?: Set<string>
}): LandingPageData {
	const {
		landingPageData,
		ctaTargetPath,
		ctaTargets = DEFAULT_FREE_TRIAL_CTA_TARGETS,
	} = params

	if (!ctaTargetPath) {
		return landingPageData
	}

	const content = landingPageData.content
	if (!Array.isArray(content)) {
		return landingPageData
	}

	return {
		...landingPageData,
		content: content.map((rawBlock) => {
			if (!rawBlock || typeof rawBlock !== 'object') {
				return rawBlock
			}

			const block = rawBlock as LandingPageBlock & LandingBlock
			const blockProps = (block.props ?? {}) as Record<string, unknown>

			if (block.type === 'HeroModern' && Array.isArray(blockProps.buttons)) {
				const buttons = blockProps.buttons as unknown[]
				return {
					...block,
					props: {
						...blockProps,
						buttons: buttons.map((button) => {
							if (!button || typeof button !== 'object') {
								return button
							}

							const buttonObject = button as Record<string, unknown>
							return {
								...buttonObject,
								href: resolveCtaHref(
									buttonObject.href,
									ctaTargetPath,
									ctaTargets,
								),
							}
						}),
					},
				} as LandingPageBlock
			}

			if (block.type === 'SimpleCta') {
				return {
					...block,
					props: {
						...blockProps,
						buttonLink: resolveCtaHref(
							blockProps.buttonLink,
							ctaTargetPath,
							ctaTargets,
						),
					},
				} as LandingPageBlock
			}

			if (block.type === 'PricingSnapshot') {
				const primaryCta =
					blockProps.primaryCta &&
					typeof blockProps.primaryCta === 'object' &&
					blockProps.primaryCta !== null
						? {
								...(blockProps.primaryCta as Record<string, unknown>),
								href: resolveCtaHref(
									(blockProps.primaryCta as Record<string, unknown>).href,
									ctaTargetPath,
									ctaTargets,
								),
							}
						: blockProps.primaryCta

				return {
					...block,
					props: {
						...blockProps,
						primaryCta,
					},
				} as LandingPageBlock
			}

			return block
		}),
	}
}

export function resolveLandingPageType(
	slug: string,
	brandKey: string,
): 'home' | 'about' | null {
	const normalizedSlug = slug.startsWith(`${brandKey}-`)
		? slug.replace(`${brandKey}-`, '')
		: slug

	if (normalizedSlug !== 'home' && normalizedSlug !== 'about') {
		return null
	}

	return normalizedSlug
}
